import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { LiftType } from "../../flatbuffers/generated/archive/lift-type";
import { AdapterProtocol } from "../../flatbuffers/generated/archive/adapter-protocol";
import { OrgStatus } from "../../flatbuffers/generated/archive/org-status";
import { WizardStep } from "./utils/wizardInvalidation";
// REDUX
import {
  useLazyGetServerQuery,
  useLazyGetServersByOrganizationQuery,
} from "../../redux/servers/serverAPI";
import { useLazyGetAdaptersQuery } from "../../redux/adapters/adapterAPI";
import { useLazyGetChannelsByServerQuery } from "../../redux/channels/channelAPI";
import { useLazyGetBanksByServerQuery } from "../../redux/banks/bankAPI";
import { useLazyGetLiftsByBankQuery } from "../../redux/lifts/liftAPI";
import { useLazyGetRolesByOrganizationQuery } from "../../redux/roles/roleAPI";
import { useLazyGetUsersByOrganizationQuery } from "../../redux/users/userAPI";
import { useLazyGetFavoriteGridsByOrganizationQuery } from "../../redux/monitoring/monitoringAPI";
import { useLazyGetNotificationsByOrganizationQuery } from "../../redux/notifications/notificationAPI";
import { useLazyGetActionCatalogsByOrganizationQuery } from "../../redux/actions/actionsAPI";
import { useLazyGetOrganizationAndChildsQuery } from "../../redux/organizations/organizationAPI";
import { useLazyGetDebounceOverridesQuery } from "../../redux/debounceOverrides/debounceOverridesAPI";
import { useLazyGetChannelModifiersByChannelQuery } from "../../redux/channelModifiers/channelModifierAPI";
// AUTH
import GETJwtToken from "../../redux/authentication/authentication";
import { computeHydratedCompletedSteps, firstIncompleteStep } from "./utils/wizardCompletion";
import { describeError } from "./utils/describeError";
import actionCatalogHasBankBinding from "./utils/actionCatalogBinding";
import settleInTurn from "./utils/settleInTurn";
import { formatUuidT } from "../../flatbuffers/display";
import { CDO_PROTOS } from "../../constants/adapter";

/**
 * Normalize an organization status that may arrive as a FlatBuffer enum number
 * (OrgStatus: DRAFT=0, ACTIVE=1) or, on the JSON path, a string. Returns the
 * canonical uppercase token, or null when unrecognized.
 *
 * Previously the hydrator called `status?.toUpperCase()` directly; under
 * FlatBuffer serialization `status` is a numeric enum, so `.toUpperCase()`
 * threw and the catch silently left orgStatus null — making every ACTIVE org
 * look like a DRAFT (and defeating the ACTIVE-org "all steps complete" path).
 */
export function normalizeOrgStatus(raw: unknown): "DRAFT" | "ACTIVE" | null {
  let name: string | undefined;
  if (typeof raw === "number") {
    name = OrgStatus[raw];
  } else if (typeof raw === "string") {
    name = raw;
  }
  const upper = name?.toUpperCase();
  return upper === "ACTIVE" || upper === "DRAFT" ? upper : null;
}

export interface PendingUser {
  email: string;
  first_name: string;
  last_name: string;
  role_id?: string;
  roleName?: string;
}

export interface WizardState {
  currentStep: number;
  organizationId: string | null;
  orgStatus: "DRAFT" | "ACTIVE" | null;
  serverId: string | null;
  adapterIds: string[];
  channelIds: string[];
  bankIds: string[];
  bankNames: Record<string, string>;
  bankTypes: Record<string, string>;
  adapterIdByBank: Record<string, string>;
  // Uppercase AdapterProtocol name (e.g. "MODBUS", "LN1", "MCE") for each
  // bank's referenced adapter. MODBUS banks arrive with no nested `adapter`
  // on the bank response — their control commands live only in the separate
  // binary CDO — so consumers (the Actions step) rely on this map to know the
  // protocol without another round-trip. Empty string when unresolved.
  adapterProtocolByBank: Record<string, string>;
  liftIdsByBank: Record<string, string[]>;
  liftNamesByBank: Record<string, Record<string, string>>;
  roleIds: string[];
  userIds: string[];
  // Full validation results per adapter (id → { valid, message }).
  // Populated by the adapter editor on save; consumed by the Review page
  // so it displays each page's own validation rather than re-validating.
  adapterValidations: Record<string, { valid: boolean; message: string }>;
  // Legacy boolean map kept for backward compat with the StepAdapters table
  // column; derived from adapterValidations when needed.
  adapterVerified: Record<string, boolean>;
  pendingUsers: PendingUser[];
  notificationIds: string[];
  actionCatalogIds: string[];
  monitorGridId: string | null;
  completedSteps: number[];
  /**
   * Monotonic per-step invalidation counter, keyed by WizardStep index. Bumped
   * every time a step is invalidated; never decremented, never reused. A step
   * index absent from the map means version 0 — the same as never invalidated.
   *
   * Deliberately independent of `completedSteps` and never derived from it:
   * several steps re-mark themselves complete on their own schedule (e.g.
   * StepLiftsFloors whenever all lifts are saved; StepChannelModifiers,
   * StepDebounce, StepBacnet, StepNotifications, StepActions, and
   * StepMonitorGrid all auto-complete on entry since they're optional). If the
   * refetch obligation were expressed as "completed is false", any of those
   * could erase it by re-completing before the operator ever returned. A
   * counter cannot be erased that way: a consumer that has not yet acted on
   * version N still sees N regardless of what happens to `completedSteps`.
   *
   * Consumers must compare for INEQUALITY against their own watermark, never
   * `>` — see utils/useWizardStepEntry.ts, whose reset-to-`{}` sites below
   * rely on this: a wholesale reset still reads as "changed" to an
   * already-mounted consumer holding a higher watermark, and correctly
   * triggers one refetch.
   */
  stepInvalidationVersions: Record<number, number>;
  adapterNames: Record<string, string>;
  hasBacnetConfig: boolean;
  hasDebounceOverrides: boolean;
  hasChannelModifiers: boolean;
}

export type DetailEntityType = "organization" | "server" | "adapter" | "channel" | "bank" | "lift";

export interface DetailDrawerState {
  open: boolean;
  entityType: DetailEntityType | null;
  entityId: string | null;
  data: Record<string, any> | null;
}

/**
 * State for the "the server this wizard targets has been deleted server-side"
 * fatal-but-recoverable condition.
 *
 * Set whenever any step's mutation/query catches a backend response with the
 * message "No server with that id exists" — that 404 is unambiguous: GORM's
 * `First(... id = ?)` filters soft-deleted rows, so the server we're trying
 * to act against has had its `deleted_at` set. The most common path that
 * produces this is `sync/bidir/apply.go:605` (`UPDATE servers SET deleted_at
 * = ? WHERE id = ?`), which bypasses GORM CASCADE and leaves channels /
 * adapters dangling on a now-soft-deleted server. The frontend has no signal
 * this happened until a write fails.
 *
 * When set, the wizard renders a top-of-page banner that explains the
 * condition in plain language and offers a one-click "Go to Server step"
 * recovery action. `attemptedAt` is the timestamp the failure was detected
 * (used to dedupe rapid duplicate detections from cascading retries).
 */
export interface StaleServerError {
  serverId: string;
  message: string;
  attemptedAt: number;
}

/**
 * Detect whether an arbitrary thrown error indicates a stale server_id.
 * Walks the same envelope shapes `formatAdapterApiError` knows about
 * (RTK Query `{status, data: {message}}`, plain Error, etc.) and returns
 * the matched message when found, null otherwise. Exported so any step's
 * error handler can call it without coupling to a specific RTK Query
 * error shape.
 */
export function detectStaleServerErrorMessage(err: unknown): string | null {
  function readMessage(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message;
    if (typeof value !== "object") return null;
    const e = value as Record<string, unknown>;
    if (typeof e.message === "string") return e.message;
    const d = e.data;
    if (d !== null && typeof d === "object") {
      const dataMsg = (d as Record<string, unknown>).message;
      if (typeof dataMsg === "string") return dataMsg;
    }
    if (typeof e.error === "string") return e.error;
    return null;
  }
  const msg = readMessage(err);
  if (!msg) return null;
  // Match the exact backend phrasing from server.controller.go / fetchServer.
  // Case-insensitive in case future i18n changes the casing.
  return /no server with that id exists/i.test(msg) ? msg : null;
}

interface WizardContextValue {
  state: WizardState;
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
  goToStep: (step: number) => void;
  markStepCompleted: (step: number) => void;
  unmarkStepCompleted: (...steps: number[]) => void;
  isStepCompleted: (step: number) => boolean;
  /** The step's current invalidation version — see `stepInvalidationVersions`
   *  on `WizardState`. 0 for a step never invalidated. Matched-pair accessor
   *  to `isStepCompleted`, so consumers (e.g. `useWizardStepRefresh`) read
   *  through the context contract instead of reaching into raw state with
   *  their own `?? 0` default. */
  getStepVersion: (step: number) => number;
  isHydrating: boolean;
  hydrationError: string | null;
  stepValidationMessage: string;
  setStepValidationMessage: (msg: string) => void;
  detailDrawer: DetailDrawerState;
  openDetailDrawer: (
    entityType: DetailEntityType,
    entityId: string,
    data?: Record<string, any>,
  ) => void;
  closeDetailDrawer: () => void;
  /** Set when any step detects a "No server with that id exists" 404 from
   *  the backend. Cleared by `clearStaleServerErrorAndReroute`. */
  staleServerError: StaleServerError | null;
  /** Report a stale-server condition. Idempotent across rapid duplicate
   *  detections — the existing record stays in place if one is already set. */
  reportStaleServerError: (msg: string) => void;
  /** One-click recovery: clear the error, clear the wizard's serverId, and
   *  navigate to the Server step so the user can pick or create a new one. */
  clearStaleServerErrorAndReroute: () => void;
}

const INITIAL_STATE: WizardState = {
  currentStep: 0,
  organizationId: null,
  orgStatus: null,
  serverId: null,
  adapterIds: [],
  channelIds: [],
  bankIds: [],
  bankNames: {},
  bankTypes: {},
  adapterIdByBank: {},
  adapterProtocolByBank: {},
  liftIdsByBank: {},
  liftNamesByBank: {},
  roleIds: [],
  userIds: [],
  pendingUsers: [],
  notificationIds: [],
  actionCatalogIds: [],
  monitorGridId: null,
  completedSteps: [],
  stepInvalidationVersions: {},
  adapterVerified: {},
  adapterValidations: {},
  adapterNames: {},
  hasBacnetConfig: false,
  hasDebounceOverrides: false,
  hasChannelModifiers: false,
};

/**
 * Bump the invalidation counter for each of `steps`. Pure and total: an empty
 * `steps` returns the original map by identity so a no-op unmark produces no
 * state churn. Shared by unmarkStepCompleted and clearStaleServerErrorAndReroute
 * so the two cannot drift out of sync with each other.
 */
function bumpStepInvalidationVersions(
  versions: Record<number, number>,
  steps: readonly number[],
): Record<number, number> {
  if (steps.length === 0) return versions;
  const next = { ...versions };
  steps.forEach((step) => {
    next[step] = (next[step] ?? 0) + 1;
  });
  return next;
}

/** Every wizard step index from `from` through Review, inclusive. */
function wizardStepsFrom(from: number): number[] {
  const out: number[] = [];
  for (let s = from; s <= WizardStep.Review; s += 1) out.push(s);
  return out;
}

const WizardContext = createContext<WizardContextValue | null>(null);

// ************************************************ */
// HYDRATOR *************************************** */
function WizardHydrator({
  orgId,
  initialStep = undefined,
  onComplete,
  onError,
}: {
  orgId: string;
  initialStep?: number;
  onComplete: () => void;
  onError: (msg: string) => void;
}) {
  const { setState, reportStaleServerError } = useWizard();

  const [triggerGetServers] = useLazyGetServersByOrganizationQuery();
  const [triggerGetServerByID] = useLazyGetServerQuery();
  const [triggerGetAdapters] = useLazyGetAdaptersQuery();
  const [triggerGetChannels] = useLazyGetChannelsByServerQuery();
  const [triggerGetBanks] = useLazyGetBanksByServerQuery();
  const [triggerGetLifts] = useLazyGetLiftsByBankQuery();
  const [triggerGetRoles] = useLazyGetRolesByOrganizationQuery();
  const [triggerGetUsers] = useLazyGetUsersByOrganizationQuery();
  const [triggerGetGrids] = useLazyGetFavoriteGridsByOrganizationQuery();
  const [triggerGetNotifications] = useLazyGetNotificationsByOrganizationQuery();
  const [triggerGetActionCatalogs] = useLazyGetActionCatalogsByOrganizationQuery();
  const [triggerGetOrg] = useLazyGetOrganizationAndChildsQuery();
  const [triggerGetDebounceOverrides] = useLazyGetDebounceOverridesQuery();
  const [triggerGetChannelModifiersByChannel] = useLazyGetChannelModifiersByChannelQuery();

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      try {
        const token = await GETJwtToken();

        // Fetch org to get its status
        let orgStatus: "DRAFT" | "ACTIVE" | null = null;
        try {
          const orgResult = await triggerGetOrg({ orgId, token }).unwrap();
          orgStatus = normalizeOrgStatus(orgResult?.data?.status);
        } catch {
          // If org fetch fails, leave status null
        }
        if (cancelled) return;

        // Fetch servers by org
        const serverResult = await triggerGetServers({
          orgId,
          token,
        }).unwrap();
        if (cancelled) return;
        const servers = serverResult?.data || [];
        /** Normalize a value that may be a UuidT object to a UUID string. */
        const uid = (v: unknown): string =>
          v && typeof v === "object" ? formatUuidT(v as any) || String(v) : String(v ?? "");

        const serverId = servers.length > 0 ? uid(servers[0].id) : null;

        // Pre-emptive existence check on the resolved serverId.
        //
        // The list-by-organization endpoint applies GORM's default scope and
        // already excludes soft-deleted servers, so under steady-state the
        // serverId we just picked IS alive. The check below is the safety
        // belt for the race condition where:
        //   (a) the server was alive when triggerGetServers fired, and
        //   (b) was soft-deleted via bidir sync between (a) and the
        //       subsequent parallel adapter/channel/bank fetches.
        // In that window, the parallel calls would return data referencing
        // a server that NO downstream write will accept. Catching it here
        // means the wizard surfaces the stale-server banner immediately
        // rather than after the user wastes time configuring an unreachable
        // server.
        //
        // The check is fault-tolerant: any non-404 error (network blip,
        // 5xx, auth refresh) is intentionally swallowed so the wizard
        // continues to hydrate. Only an unambiguous 404 with the canonical
        // "No server with that id exists" envelope triggers the banner.
        if (serverId) {
          try {
            await triggerGetServerByID({ idServer: serverId, token }).unwrap();
          } catch (preCheckErr: unknown) {
            const staleMsg = detectStaleServerErrorMessage(preCheckErr);
            if (staleMsg) {
              if (cancelled) return;
              reportStaleServerError(staleMsg);
              // Hydration is logically complete — there is nothing left to
              // resolve against a dead server. The banner-driven recovery
              // (clearStaleServerErrorAndReroute) is the next user action.
              return;
            }
            // Non-404 (e.g. transient network error) — fall through so the
            // parallel fetches can still run; they may succeed.
          }
        }

        let adapterIds: string[] = [];
        let channelIds: string[] = [];
        let channelItems: any[] = [];
        let bankIds: string[] = [];
        const bankNames: Record<string, string> = {};
        const bankTypes: Record<string, string> = {};
        const liftIdsByBank: Record<string, string[]> = {};
        const liftNamesByBank: Record<string, Record<string, string>> = {};
        const adapterIdByBank: Record<string, string> = {};
        const adapterProtocolByBank: Record<string, string> = {};
        const adapterProtocolById: Record<string, string> = {};
        const adapterNameMap: Record<string, string> = {};
        const adapterVerifiedMap: Record<string, boolean> = {};
        let hasBacnetConfig = false;
        let hasDebounceOverrides = false;
        let hasChannelModifiers = false;

        if (serverId) {
          // Fetch adapters, channels, and banks in parallel. Adapters are
          // org-global (adapters.server_id / org_id were removed); channels and
          // banks are server-scoped. The full adapter list fetched here is only
          // the METADATA source (name / protocol / verified / bacnet) — WHICH
          // adapters belong to THIS org is derived from the banks below, never
          // from the global list.
          const [adapterResult, channelResult, bankResult] = await Promise.all([
            triggerGetAdapters({
              page: 1,
              limit: 9999,
              searchAndFilterString: "",
              token,
            }).unwrap(),
            triggerGetChannels({
              serverId,
              page: 1,
              limit: 9999,
              searchAndFilterString: "",
              token,
            }).unwrap(),
            triggerGetBanks({
              idServer: serverId,
              page: 1,
              limit: 9999,
              searchAndFilterString: "",
              token,
            }).unwrap(),
          ]);

          if (cancelled) return;

          const allAdapters: any[] = adapterResult?.data || [];
          channelItems = channelResult?.data || [];
          channelIds = channelItems.map((c: any) => uid(c.id));
          const banks = bankResult?.data || [];
          bankIds = banks.map((b: any) => uid(b.id));
          banks.forEach((b: any) => {
            bankNames[uid(b.id)] = b.name || uid(b.id);
          });
          banks.forEach((b: any) => {
            bankTypes[uid(b.id)] =
              typeof b.type === "number"
                ? (LiftType[b.type as LiftType] ?? LiftType[LiftType.ELEVATOR])
                : b.type || LiftType[LiftType.ELEVATOR];
          });
          banks.forEach((b: any) => {
            const aId = b.adapterId ?? b.adapter_id;
            if (aId) adapterIdByBank[uid(b.id)] = uid(aId);
          });

          // Scope the org's adapters to exactly the ones its banks reference.
          // Adapters are shared/global (no org_id), so the ONLY org↔adapter link
          // is bank.adapter_id -> bank.channel_id -> channel.server_id ->
          // server.org_id. This mirrors the backend's authoritative
          // wizexport.orgAdapterIDsSubquery and StepReview's publish scoping, so
          // the Adapters step shows the adapters this deployment actually USES
          // and never the entire global pool of every other org's adapters.
          const referencedAdapterIds = new Set<string>(
            Object.values(adapterIdByBank).filter((id) => Boolean(id)),
          );
          const referencedAdapters = allAdapters.filter((a: any) =>
            referencedAdapterIds.has(uid(a.id)),
          );
          adapterIds = referencedAdapters.map((a: any) => uid(a.id));

          referencedAdapters.forEach((a: any) => {
            const id = uid(a.id);
            const name = typeof a.name === "string" ? a.name : "";
            adapterNameMap[id] = name;
            // Lightweight verified heuristic: a real name means the adapter was
            // configured.  For protocols that carry CDO configuration (MCE, LN1,
            // etc.) we intentionally leave the map entry unset so the status
            // column shows "Pending" until StepAdapters validates CDO content.
            const proto = (
              typeof a.protocolType === "number"
                ? (AdapterProtocol[a.protocolType as AdapterProtocol] ?? "")
                : a.protocol_type || ""
            ).toUpperCase();
            adapterProtocolById[id] = proto;
            if (!CDO_PROTOS.has(proto)) {
              adapterVerifiedMap[id] = Boolean(name) && name !== id;
            }
            // BACnet config detection: any non-empty BACnet JSON on a referenced adapter.
            const bacnetValue =
              a.bacnetConfigJson ??
              a.bacnet_config ??
              a.bacnetBankMappingsJson ??
              a.bacnet_bank_mappings;
            const bacnetStr = bacnetValue ? String(bacnetValue) : "";
            if (bacnetStr.trim() !== "" && bacnetStr !== "{}") {
              hasBacnetConfig = true;
            }
          });

          // Project the per-adapter protocol onto each bank via its
          // adapter_id link, so the Actions step can tell a MODBUS bank apart
          // even though the bank response carries no nested adapter.
          Object.entries(adapterIdByBank).forEach(([bankId, adapterId]) => {
            const proto = adapterProtocolById[adapterId];
            if (proto) adapterProtocolByBank[bankId] = proto;
          });

          // Per-bank lifts and debounce overrides, and per-channel modifiers.
          // Each trigger is walked one argument at a time (settleInTurn
          // explains why firing one lazy trigger in parallel loses every
          // result but the last); the three walks run alongside each other.
          const [liftSettled, debounceResults, modifierResults] = await Promise.all([
            settleInTurn(banks, (bank: any) =>
              triggerGetLifts({
                bankId: uid(bank.id),
                page: 1,
                limit: 9999,
                searchAndFilterString: "",
                token,
              }).unwrap(),
            ),
            // Debounce overrides only have a per-bank endpoint.
            settleInTurn(banks, (bank: any) =>
              triggerGetDebounceOverrides({ bankId: uid(bank.id), token }).unwrap(),
            ),
            // Modifiers are keyed by channel, and the /channel_modifiers/ list
            // has no server_id filter, so each channel is probed for >=1.
            settleInTurn(channelIds, (channelId) =>
              triggerGetChannelModifiersByChannel({ channelId, token }).unwrap(),
            ),
          ]);
          if (cancelled) return;

          // A failed lift fetch fails hydration, as it always has: an org shown
          // with a bank's lifts silently missing is worse than an error. The
          // two probes tolerate a failure, which just reads as "none found".
          liftSettled.forEach((r, i) => {
            if (r.status === "rejected") throw r.reason;
            const lifts = r.value?.data || [];
            if (lifts.length > 0) {
              const bId = uid(banks[i].id);
              liftIdsByBank[bId] = lifts.map((l: any) => uid(l.id));
              liftNamesByBank[bId] = Object.fromEntries(
                lifts.map((l: any) => [uid(l.id), l.name || uid(l.id)]),
              );
            }
          });
          const anyFound = (results: PromiseSettledResult<any>[]) =>
            results.some((r) => {
              if (r.status !== "fulfilled") return false;
              const items = r.value?.data?.data ?? r.value?.data ?? r.value ?? [];
              return Array.isArray(items) && items.length > 0;
            });
          hasDebounceOverrides = anyFound(debounceResults);
          hasChannelModifiers = anyFound(modifierResults);
          if (cancelled) return;
        }

        // Fetch roles, users, notifications, actions, and monitor grids in parallel
        // Use allSettled so individual failures don't break the entire hydration
        const settled = await Promise.allSettled([
          triggerGetRoles({ orgId, token }).unwrap(),
          triggerGetUsers({
            orgId,
            page: 1,
            limit: 9999,
            searchAndFilterString: "",
            token,
          }).unwrap(),
          triggerGetNotifications({ orgId, token }).unwrap(),
          triggerGetActionCatalogs({ orgId, token }).unwrap(),
          triggerGetGrids({ idOrganization: orgId, token }).unwrap(),
        ]);

        if (cancelled) return;

        const val = (i: number) =>
          settled[i].status === "fulfilled"
            ? (settled[i] as PromiseFulfilledResult<any>).value
            : null;

        const roleIds = (val(0)?.data || []).map((r: any) => uid(r.id));
        const userIds = (val(1)?.data || []).map((u: any) => uid(u.id));
        const notifRaw = val(2);
        const notificationIds = (Array.isArray(notifRaw) ? notifRaw : notifRaw?.data || []).map(
          (n: any) => uid(n.id),
        );
        const actionRaw = val(3);
        // Only bank-bound catalogs count toward `actionCatalogIds`. Bank-less
        // "phantom" catalogs (legacy decode-fail-to-CarCallLock rows) are
        // invisible in the Actions table but would otherwise trip the mount
        // sweep's "actions already exist" skip and suppress auto-generation for
        // banks that have no real actions yet. See actionCatalogHasBankBinding.
        const actionCatalogIds = (Array.isArray(actionRaw) ? actionRaw : actionRaw?.data || [])
          .filter(actionCatalogHasBankBinding)
          .map((a: any) => uid(a.id));
        const gridData = val(4);
        const grids = Array.isArray(gridData) ? gridData : gridData?.data || [];
        const monitorGridId = grids.length > 0 ? uid(grids[0].id) : null;

        // Determine completed steps based on existing data. The pure
        // logic lives in utils/wizardCompletion.ts so it can be unit-
        // tested without the RTK Query / async machinery here.
        //
        // notificationIds, actionCatalogIds, and monitorGridId are
        // hydrated above and stored on state below for the per-step
        // pages to render existing configurations, but they are
        // intentionally NOT inputs to the completion calculation —
        // those three steps are optional and auto-complete from the
        // prerequisite chain. See computeHydratedCompletedSteps for
        // the full reasoning.
        const completedSteps = computeHydratedCompletedSteps({
          serverId,
          adapterIds,
          channelIds,
          channelItems,
          bankIds,
          liftIdsByBank,
          roleIds,
          userIds,
          orgStatus,
        });

        const firstIncomplete = firstIncompleteStep(completedSteps, 12);

        setState({
          currentStep:
            initialStep !== undefined && !Number.isNaN(initialStep) ? initialStep : firstIncomplete,
          organizationId: orgId,
          orgStatus,
          serverId,
          adapterIds,
          channelIds,
          bankIds,
          bankNames,
          bankTypes,
          adapterIdByBank,
          adapterProtocolByBank,
          liftIdsByBank,
          liftNamesByBank,
          roleIds,
          userIds,
          pendingUsers: [],
          notificationIds,
          actionCatalogIds,
          monitorGridId,
          completedSteps,
          // Fresh hydration is a clean slate: everything just loaded from the
          // server is maximally current, so there is nothing meaningful to
          // carry forward from any prior invalidation state.
          stepInvalidationVersions: {},
          adapterVerified: adapterVerifiedMap,
          adapterValidations: {},
          adapterNames: adapterNameMap,
          hasBacnetConfig,
          hasDebounceOverrides,
          hasChannelModifiers,
        });
      } catch (err) {
        // On error, set orgId and start at step 1 (server)
        setState((prev) => ({
          ...prev,
          organizationId: orgId,
          completedSteps: [0],
          currentStep: 1,
          // Not compiler-forced (this is a `prev =>` merge, not a literal) —
          // added deliberately. A failed hydration is also a fresh start.
          stepInvalidationVersions: {},
        }));
        // `err` here is frequently an RTK Query error object (FetchBaseQueryError
        // / SerializedError), NOT an Error instance — `err instanceof Error`
        // misses those and collapsed everything to "Unknown error". describeError
        // pulls out the HTTP status and server-side message (e.g. the exact failing
        // endpoint's "HTTP 400 — invalid scope ID") so the banner says what broke.
        onError(
          `Failed to resume wizard — ${describeError(err)}. Some progress may not have loaded.`,
        );
      }

      onComplete();
    }

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return null;
}

// ************************************************ */
// PROVIDER *************************************** */
export function WizardProvider({
  children,
  resumeOrgId = null,
  initialStep = undefined,
}: {
  children: React.ReactNode;
  resumeOrgId?: string | null;
  initialStep?: number;
}) {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [isHydrating, setIsHydrating] = useState(!!resumeOrgId);
  const [hydrationError, setHydrationError] = useState<string | null>(null);
  const [stepValidationMessage, setStepValidationMessage] = useState("");
  const [staleServerError, setStaleServerError] = useState<StaleServerError | null>(null);
  const [detailDrawer, setDetailDrawer] = useState<DetailDrawerState>({
    open: false,
    entityType: null,
    entityId: null,
    data: null,
  });

  const openDetailDrawer = (
    entityType: DetailEntityType,
    entityId: string,
    data?: Record<string, any>,
  ) => {
    setDetailDrawer({ open: true, entityType, entityId, data: data || null });
  };

  const closeDetailDrawer = () => {
    setDetailDrawer((prev) => ({ ...prev, open: false }));
  };

  const goToStep = (step: number) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  };

  const markStepCompleted = (step: number) => {
    setState((prev) => {
      if (prev.completedSteps.includes(step)) return prev;
      return { ...prev, completedSteps: [...prev.completedSteps, step] };
    });
  };

  const unmarkStepCompleted = (...steps: number[]) => {
    setState((prev) => ({
      ...prev,
      completedSteps: prev.completedSteps.filter((s) => !steps.includes(s)),
      // Bump unconditionally, even for a step that was not in completedSteps.
      // "This step's data is stale" and "this step is not marked done" are
      // different claims: a step can be invalidated while it was never
      // completed (the optional steps auto-complete only on visit), and it
      // still owes a refetch when the operator next arrives.
      stepInvalidationVersions: bumpStepInvalidationVersions(prev.stepInvalidationVersions, steps),
    }));
  };

  const isStepCompleted = (step: number) => state.completedSteps.includes(step);

  const getStepVersion = (step: number) => state.stepInvalidationVersions[step] ?? 0;

  /**
   * Mark the wizard's current serverId as stale (deleted server-side).
   * Idempotent — if an error is already set, keep the original so the user
   * sees the FIRST occurrence rather than a noisy stream of duplicates from
   * retrying mutations.
   */
  const reportStaleServerError = (msg: string) => {
    setStaleServerError((prev) => {
      if (prev !== null) return prev;
      return {
        serverId: state.serverId ?? "",
        message: msg,
        attemptedAt: Date.now(),
      };
    });
  };

  /**
   * One-click recovery: clear the staleness flag, drop the now-invalid
   * serverId from wizard state, and navigate the user to the Server step so
   * they can pick or create a new server. Also unmarks Server and all
   * downstream steps as "completed" because the underlying entity they
   * depended on no longer exists.
   */
  const clearStaleServerErrorAndReroute = () => {
    setStaleServerError(null);
    setState((prev) => ({
      ...prev,
      serverId: null,
      currentStep: WizardStep.Server,
      completedSteps: prev.completedSteps.filter((s) => s < WizardStep.Server),
      // Bump the same range the filter above drops — Server onward — rather
      // than only the steps that happened to be marked complete. The server
      // these steps were configured against is gone, so a mounted-but-never-
      // completed step's data is just as stale as a completed one's.
      stepInvalidationVersions: bumpStepInvalidationVersions(
        prev.stepInvalidationVersions,
        wizardStepsFrom(WizardStep.Server),
      ),
    }));
  };

  const value = useMemo(
    () => ({
      state,
      setState,
      goToStep,
      markStepCompleted,
      unmarkStepCompleted,
      isStepCompleted,
      getStepVersion,
      isHydrating,
      hydrationError,
      stepValidationMessage,
      setStepValidationMessage,
      detailDrawer,
      openDetailDrawer,
      closeDetailDrawer,
      staleServerError,
      reportStaleServerError,
      clearStaleServerErrorAndReroute,
    }),
    [state, isHydrating, hydrationError, stepValidationMessage, detailDrawer, staleServerError],
  );

  return (
    <WizardContext.Provider value={value}>
      {resumeOrgId && isHydrating && (
        <WizardHydrator
          orgId={resumeOrgId}
          initialStep={initialStep}
          onComplete={() => setIsHydrating(false)}
          onError={(msg) => setHydrationError(msg)}
        />
      )}
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard(): WizardContextValue {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error("useWizard must be used within a WizardProvider");
  }
  return context;
}

/**
 * Like {@link useWizard} but returns `null` instead of throwing when the
 * component is rendered outside a `WizardProvider`. Useful for components
 * (e.g. AdapterEditor) that are used both INSIDE the wizard and as
 * standalone edge-management pages — they can call this to opportunistically
 * report stale-server-id errors when running inside the wizard, while
 * remaining functional standalone.
 */
export function useWizardOptional(): WizardContextValue | null {
  return useContext(WizardContext);
}
